import { ClipboardIcon, CheckCircleIcon } from "@heroicons/react/24/solid";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { FC } from "react";

import { UppdragStatus } from "types/uppdrag.type";
import { cn } from "helpers/classNames";

interface Props {
  status: UppdragStatus;
  className?: string;
  disabled?: boolean;
}

const containerBaseClass =
  "inline-flex items-center gap-x-0.5 rounded-md md:pl-1.5 pl-2.5 pr-3 md:pr-2 py-1 text-sm md:text-xs font-medium ring-1 ring-inset";
const iconBaseClass = "group relative -ml-1 h-3.5 sm:h-4 w-auto rounded-sm";

export const UppdragStatusTag: FC<Props> = ({
  status,
  className,
  disabled,
}) => (
  <span
    className={cn(
      containerBaseClass,
      getUppdragStatusTagContainerClass(status, disabled),
      className,
    )}
  >
    <UppdragStatusIcon status={status} disabled={disabled} />
    {getUppdragStatusLabel(status)}
  </span>
);

export const getUppdragStatusTagContainerClass = (
  status: UppdragStatus,
  disabled?: boolean,
) => {
  switch (status) {
    case UppdragStatus.Scheduled:
      return "bg-gray-50 text-gray-900 ring-gray-600/20";
    case UppdragStatus.Started:
      return "bg-blue-50 text-blue-900 ring-blue-600/30";
    case UppdragStatus.Completed:
      return disabled
        ? "bg-gray-25 text-gray-300 ring-gray-300/10"
        : "bg-cyan-50 text-cyan-900 ring-cyan-600/40";
  }
};

export const UppdragStatusIcon: FC<Props> = ({
  status,
  className,
  disabled,
}) => (
  <>
    {status === UppdragStatus.Scheduled ? (
      <CalendarDaysIcon
        className={cn("text-gray-400", iconBaseClass, className)}
        aria-hidden="true"
      />
    ) : status === UppdragStatus.Started ? (
      <ClipboardIcon
        className={cn("text-blue-400", iconBaseClass, className)}
        aria-hidden="true"
      />
    ) : status === UppdragStatus.Completed ? (
      <CheckCircleIcon
        className={cn(
          "text-cyan-500",
          iconBaseClass,
          className,
          disabled && "text-gray-300",
        )}
        aria-hidden="true"
      />
    ) : (
      "missing"
    )}
  </>
);

export const getUppdragStatusLabel = (status: UppdragStatus) => {
  switch (status) {
    case UppdragStatus.Scheduled:
      return "Skapat";
    case UppdragStatus.Started:
      return "Påbörjat";
    case UppdragStatus.Completed:
      return "Slutfört";
  }
};
